import React, { useEffect, useMemo, useState } from "react";

import ContentPaste from "@mui/icons-material/ContentPaste";
import { InputAdornment } from "@mui/material";
import { add, sub } from "date-fns";
import { useFormContext } from "react-hook-form";

import useGetServiceTeams from "@fartherfinance/frontend/api/Entity/hooks/useGetServiceTeams";
import { TASK_TITLE_MAX_LENGTH } from "@fartherfinance/frontend/api/Tasks/constants/common";
import {
  ManualTaskTypeV2,
  TaskPriority,
  TaskStatus,
} from "@fartherfinance/frontend/api/Tasks/Types";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import {
  EnhancedAutocompleteOption,
  WithGroupMeta,
} from "@src/multiCustodian/pages/Advisor/common";
import {
  assigneeDefaultOption,
  custodianOptions,
  MIN_MAX_DUE_DATE_RANGE_YEARS,
  TASK_TITLE_MAX_LENGTH_ERROR_MSG,
  TaskAssigneeAutocompleteOption,
  taskPriorityOptions,
} from "@src/multiCustodian/pages/Advisor/Tasks/common";
import {
  useAdvisorsOptions,
  useAssigneesOptions,
  useClientsOptions,
  useServiceTeamsOptions,
  useTaskStatusOptionsV2,
  useTaskTypeOptionsV2,
} from "@src/multiCustodian/pages/Advisor/Tasks/hooks";
import InputHelperText from "@src/multiCustodian/pages/Advisor/Tasks/InputHelperText/InputHelperText";
import TaskPriorityIcon from "@src/multiCustodian/pages/Advisor/Tasks/TaskPriorityIcon/TaskPriorityIcon";
import TaskStatusIcon from "@src/multiCustodian/pages/Advisor/Tasks/TaskStatusIcon/TaskStatusIcon";
import {
  isDateValid,
  resolveServiceTeam,
} from "@src/multiCustodian/pages/Advisor/Tasks/utils";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import FormBody from "@src/sharedComponents/Forms/FormBody";
import FormBodySpanAllColumns from "@src/sharedComponents/Forms/FormBodySpanAllColumns";
import FormDateField from "@src/sharedComponents/Forms/FormDateField";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormTextareaField from "@src/sharedComponents/Forms/FormTextareaField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import Spacer from "@src/sharedComponents/Forms/Spacer";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "@src/multiCustodian/pages/Advisor/Tasks/CreateTask/CreateTask.module.css";

// ---------------------------------------------
// TYPES
// ---------------------------------------------
export interface TaskFormData {
  title: string;
  description: string;
  parentTaskTitle?: string;
  taskType: EnhancedAutocompleteOption<ManualTaskTypeV2> | null;
  serviceTeam: EnhancedAutocompleteOption<string> | null;
  advisor: EnhancedAutocompleteOption<string> | null;
  assignee: TaskAssigneeAutocompleteOption | null;
  client: EnhancedAutocompleteOption<string> | null;
  dueDate: Date | null;
  custodian: EnhancedAutocompleteOption<string> | null;
  taskStatus: EnhancedAutocompleteOption<TaskStatus>;
  taskPriority: EnhancedAutocompleteOption<TaskPriority>;
}

interface TaskFormV3Props {
  /** Controls whether service team field is rendered (true for CX). */
  isServiceTeamFieldVisible: boolean;
  /** If creating a subtask, enable additional UI bits. */
  isSubtaskCreation?: boolean;
  /** Parent task description passed only for subtasks so we can paste it. */
  parentDescription?: string;
  /** Parent task title (read only). */
  parentTaskTitle?: string;
  /** Disable task type field (used for root tasks in transition workflow). */
  disableTaskType?: boolean;
  /** Hide client field (used in transition workflows). */
  hideClientField?: boolean;
  /** Hide custodian field (used in transition workflows). */
  hideCustodianField?: boolean;
  /** Hide advisor field (used in transition workflows). */
  hideAdvisorField?: boolean;
  /** Disable title field (used for root tasks in transition workflow). */
  disableTitleField?: boolean;
}

// ---------------------------------------------
// COMPONENT
// ---------------------------------------------
const TaskFormV3: React.FC<TaskFormV3Props> = ({
  isServiceTeamFieldVisible,
  isSubtaskCreation,
  parentDescription,
  disableTaskType = false,
  hideClientField = false,
  hideCustodianField = false,
  hideAdvisorField = false,
  disableTitleField = false,
}) => {
  // Access the RHF context provided by the parent component
  const form = useFormContext<TaskFormData>();

  // Local state for helper text visibility
  const [isHelperTextVisible, setIsHelperTextVisible] = useState(false);

  // -------------------- HOOKS & OPTIONS --------------------
  const { taskTypeFormOptions } = useTaskTypeOptionsV2(
    form.watch("taskStatus")?.value
  );

  // Only pass standard task types to useTaskStatusOptionsV2 to avoid undefined errors
  const currentTaskType = form.watch("taskType")?.value;
  const isStandardTaskType =
    currentTaskType &&
    Object.values(ManualTaskTypeV2.enum).includes(currentTaskType);
  const standardTaskType = isStandardTaskType
    ? (currentTaskType as ManualTaskTypeV2)
    : undefined;

  const { taskStatusFormOptions } = useTaskStatusOptionsV2(standardTaskType);

  const { serviceTeamsOptions } = useServiceTeamsOptions();
  const { advisorsOptions, advisorsQuery } = useAdvisorsOptions();
  const { clientsOptions, clientsQuery } = useClientsOptions();
  const auth = useAdvisorRequestAuth();
  const serviceTeams = useGetServiceTeams(auth);

  // Resolve service team automatically for advisors where required
  const resolvedServiceTeam = resolveServiceTeam(
    serviceTeams.data ?? [],
    standardTaskType,
    isServiceTeamFieldVisible
  );

  // Watch selected values used further down
  const [
    formTaskStatus,
    formTaskPriority,
    formServiceTeam,
    formAssignee,
    formAdvisor,
  ] = form.watch([
    "taskStatus",
    "taskPriority",
    "serviceTeam",
    "assignee",
    "advisor",
  ]);

  // Compute assignees – same logic as in CreateTaskV3
  const fallbackAdvisorId = !isServiceTeamFieldVisible
    ? auth?.advisorId
    : undefined;
  const assigneesQueryParams = useMemo(() => {
    return {
      serviceTeam: formServiceTeam?.value,
      advisor: formAdvisor ? formAdvisor.value : fallbackAdvisorId,
    } as const;
  }, [formServiceTeam, formAdvisor, fallbackAdvisorId]);

  const { assigneesOptions, assigneesQuery } = useAssigneesOptions(
    assigneesQueryParams as any,
    true
  );
  const extendedAssigneesOptions = useMemo(
    () => [assigneeDefaultOption, ...assigneesOptions],
    [assigneesOptions]
  );

  // Date constraints
  const minDueDate = sub(new Date(), { years: MIN_MAX_DUE_DATE_RANGE_YEARS });
  const maxDueDate = add(new Date(), { years: MIN_MAX_DUE_DATE_RANGE_YEARS });

  // -------------------- WATCHERS ---------------------------
  useEffect(() => {
    const sub = form.watch((values, { name, type }) => {
      if (type === "change" && name === "description") {
        setIsHelperTextVisible((values.description ?? "").length > 2);
      }

      // Clear assignee when service team or advisor changes
      if (
        (name === "serviceTeam" || name === "advisor") &&
        values.assignee?.value
      ) {
        form.resetField("assignee", { defaultValue: null });
      }

      // Clear service team when task type changes
      if (name === "taskType" && values.serviceTeam) {
        form.resetField("serviceTeam");
      }
    });

    return () => sub.unsubscribe();
  }, [form]);

  // Auto-set resolved service team (CX) when loaded
  useEffect(() => {
    if (!resolvedServiceTeam) {
      return;
    }

    const resolvedId =
      resolvedServiceTeam.serviceTeamId ?? (resolvedServiceTeam as any).id;
    const currentId = form.getValues("serviceTeam")?.value;

    if (currentId !== resolvedId) {
      form.setValue("serviceTeam", {
        label: resolvedServiceTeam.name,
        value: resolvedId,
      });
    }
  }, [resolvedServiceTeam, form]);

  // -------------------- HELPERS ----------------------------
  const handleClearAutocompleteField = (field: keyof TaskFormData): void => {
    form.setValue(field, null);
  };

  const handlePasteDescFromParent = (): void => {
    if (!parentDescription) {
      return;
    }
    const current = form.getValues("description");
    const value = current
      ? `${current}\n${parentDescription}`
      : parentDescription;
    form.setValue("description", value);
  };

  // -------------------- RENDER -----------------------------
  return (
    <FormBody>
      {/* TITLE FIELD */}
      <FormBodySpanAllColumns>
        <FormTextField
          name="title"
          label="Title"
          required="Must not be empty"
          disabled={disableTitleField}
          rules={{
            validate: {
              maxLen: (v: string) =>
                v.length > TASK_TITLE_MAX_LENGTH
                  ? TASK_TITLE_MAX_LENGTH_ERROR_MSG
                  : true,
            },
          }}
        />
      </FormBodySpanAllColumns>

      {/* DESCRIPTION FIELD + paste from parent */}
      <FormBodySpanAllColumns>
        <div className={styles.description}>
          <FormTextareaField
            name="description"
            label="Description (Optional)"
            placeholder="Describe the task and provide additional details..."
            minRows={5}
          />

          {isSubtaskCreation && (
            <div className={styles.paste}>
              <Tooltip
                tooltipText="Parent task description is blank"
                placement="top"
                disableHoverListener={Boolean(parentDescription)}
              >
                <div>
                  <Button
                    text="Paste From Parent"
                    startIcon={<ContentPaste />}
                    onClick={handlePasteDescFromParent}
                    size="small"
                    variant="outlined"
                    buttonType="secondary"
                    disabled={!parentDescription}
                  />
                </div>
              </Tooltip>
            </div>
          )}
        </div>
        <Spacer verticalSpacing="4px" />
        <InputHelperText isVisible={isHelperTextVisible} />
      </FormBodySpanAllColumns>

      {isSubtaskCreation && (
        <FormTextField name="parentTaskTitle" label="Parent Task" readonly />
      )}

      {/* TASK TYPE */}
      <FormDropdownField<EnhancedAutocompleteOption>
        name="taskType"
        label="Type"
        disabled={disableTaskType}
        values={taskTypeFormOptions}
        required="Please select a task type"
        getOptionKey={(option) => option.value}
        onClear={() => handleClearAutocompleteField("taskType")}
      />

      {/* SERVICE TEAM / ADVISOR / ASSIGNEE VARIANTS */}
      {isServiceTeamFieldVisible ? (
        <>
          <FormDropdownField<EnhancedAutocompleteOption>
            name="serviceTeam"
            label={
              formAssignee && formAssignee.value
                ? "Service Team (Optional)"
                : "Service Team"
            }
            values={serviceTeamsOptions}
            required={
              formAssignee && formAssignee.value
                ? false
                : "Please select a service team"
            }
            isLoading={serviceTeams.isLoading}
            getOptionKey={(option) => option.value}
            onClear={() => handleClearAutocompleteField("serviceTeam")}
          />

          {!hideAdvisorField && (
            <FormDropdownField<EnhancedAutocompleteOption>
              name="advisor"
              label="Advisor"
              required="Please select an advisor"
              values={advisorsOptions}
              isLoading={advisorsQuery.isLoading}
              getOptionKey={(option) => option.value}
              onClear={() => handleClearAutocompleteField("advisor")}
            />
          )}

          <FormDropdownField<TaskAssigneeAutocompleteOption<string | undefined>>
            name="assignee"
            label={formServiceTeam ? "Assignee (Optional)" : "Assignee"}
            values={extendedAssigneesOptions}
            required={formServiceTeam ? false : "Please select an assignee"}
            isLoading={assigneesQuery.isLoading}
            getOptionKey={(option) => option.value ?? "undefined"}
            onClear={() => handleClearAutocompleteField("assignee")}
          />
        </>
      ) : (
        <>
          {!hideAdvisorField && (
            <FormDropdownField<EnhancedAutocompleteOption>
              name="advisor"
              label="Advisor"
              required="Please select an advisor"
              values={advisorsOptions}
              isLoading={advisorsQuery.isLoading}
              getOptionKey={(option) => option.value}
              onClear={() => handleClearAutocompleteField("advisor")}
            />
          )}

          <FormDropdownField<TaskAssigneeAutocompleteOption<string | undefined>>
            name="assignee"
            label="Assignee"
            values={extendedAssigneesOptions}
            required="Please select an assignee"
            isLoading={assigneesQuery.isLoading}
            getOptionKey={(option) => option.value ?? "undefined"}
            onClear={() => handleClearAutocompleteField("assignee")}
          />
        </>
      )}

      {/* CLIENT */}
      {!hideClientField && (
        <FormDropdownField<EnhancedAutocompleteOption>
          name="client"
          label="Client (Optional)"
          values={clientsOptions}
          isLoading={clientsQuery.isLoading}
          getOptionKey={(option) => option.value}
          onClear={() => handleClearAutocompleteField("client")}
        />
      )}

      {/* DUE DATE */}
      <FormDateField
        name="dueDate"
        label="Due Date"
        placeholder=""
        helperText="MM/DD/YYYY"
        minDate={minDueDate}
        maxDate={maxDueDate}
        required="Please select a due date"
        rules={{
          validate: { isDateValid },
        }}
      />

      {/* CUSTODIAN */}
      {!hideCustodianField && (
        <FormDropdownField<EnhancedAutocompleteOption>
          name="custodian"
          label="Custodian (Optional)"
          values={custodianOptions}
          getOptionKey={(option) => option.value}
          onClear={() => handleClearAutocompleteField("custodian")}
        />
      )}

      {/* STATUS & PRIORITY – NOT SHOWN FOR SUBTASK CREATION IN TASKS FLOW */}
      {!isSubtaskCreation && (
        <FormDropdownField<
          EnhancedAutocompleteOption<TaskStatus> & WithGroupMeta
        >
          name="taskStatus"
          label="Status"
          values={taskStatusFormOptions}
          getOptionKey={(option) => option.value}
          groupBy={(option) => option.meta.group}
          renderOption={(optionProps, option) => (
            <li
              {...optionProps}
              className={toClassName(optionProps.className, styles.option)}
            >
              <TaskStatusIcon taskStatus={option.value} />
              <Text variant="body">{option.label}</Text>
            </li>
          )}
          renderStartAdornment={() => (
            <InputAdornment position="start">
              <TaskStatusIcon taskStatus={formTaskStatus.value} />
            </InputAdornment>
          )}
        />
      )}

      <FormDropdownField<EnhancedAutocompleteOption<TaskPriority>>
        name="taskPriority"
        label="Priority"
        values={taskPriorityOptions}
        getOptionKey={(option) => option.value}
        renderOption={(optionProps, option) => (
          <li
            {...optionProps}
            className={toClassName(optionProps.className, styles.option)}
          >
            <TaskPriorityIcon value={option.value} />
            <Text variant="body">{option.label}</Text>
          </li>
        )}
        renderStartAdornment={() => (
          <InputAdornment position="start">
            <TaskPriorityIcon value={formTaskPriority.value} />
          </InputAdornment>
        )}
      />
    </FormBody>
  );
};

export default TaskFormV3;
